import type { CompositeFilterModel } from "../models/CompositeFilterModel";
import { isCompositeFilter } from "../models/CompositeFilterModel";

// Structural shape so both LeadsSearchFilterInstance and SearchFilterInstance pass without fighting the
// invariant `filter` generic — this driver only reads the key and the composite contract off the model.
type ConfigItem = { readonly key: PropertyKey; readonly filter?: unknown };
type CompositeItem = { readonly key: PropertyKey; readonly filter: CompositeFilterModel };

const isCompositeItem = (item: ConfigItem): item is CompositeItem => isCompositeFilter(item.filter);

const getComposites = (config: readonly ConfigItem[]): readonly CompositeItem[] => config.filter(isCompositeItem);

/**
 * Panel boundary (read): folds a flat panel value into a grouped one — each composite's member field values
 * move under the composite key as a nested fragment (via the model's `collect`), everything else stays flat.
 * Generic over any composite filter; the per-composite projection logic lives on the model, not here.
 * Symmetric to {@link ungroupDynamicFilterFields}. The DTO ↔ flat-panel adapters stay field-atomic.
 * Generic over the panel value shape so the framework stays free of any feature DTO type.
 */
export const groupDynamicFilterFields = <TValue extends object>(
    flat: TValue,
    config: readonly ConfigItem[],
): TValue => {
    const composites = getComposites(config);

    if (!composites.length) {
        return flat;
    }

    const fieldKeys = new Set<string>();
    for (const composite of composites) {
        for (const fieldKey of composite.filter.getFieldKeys()) {
            fieldKeys.add(fieldKey);
        }
    }

    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(flat)) {
        if (!fieldKeys.has(key)) {
            result[key] = value;
        }
    }

    for (const composite of composites) {
        const value = composite.filter.collect(flat as Readonly<Record<string, unknown>>);

        if (Object.keys(value).length) {
            result[String(composite.key)] = value;
        }
    }

    return result as TValue;
};

/**
 * Panel boundary (write): folds a grouped panel value back into a flat one — each composite fragment is
 * spread to its member field keys (via the model's `expand`). Explicit flat keys win over fragment-derived
 * ones, so an external flat write (e.g. the AI agent) is not clobbered by a stale fragment. Symmetric to
 * {@link groupDynamicFilterFields}.
 * Generic over the panel value shape so the framework stays free of any feature DTO type.
 */
export const ungroupDynamicFilterFields = <TValue extends object>(
    grouped: TValue,
    config: readonly ConfigItem[],
): TValue => {
    const composites = getComposites(config);

    if (!composites.length) {
        return grouped;
    }

    const byKey = new Map(composites.map((composite) => [String(composite.key), composite.filter]));

    const flat: Record<string, unknown> = {};
    const explicit: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(grouped)) {
        const composite = byKey.get(key);

        if (composite) {
            Object.assign(flat, composite.expand(value as Record<string, unknown>));
        } else {
            explicit[key] = value;
        }
    }

    return { ...flat, ...explicit } as TValue;
};
